// File: src/components/auth/VerifyEmail.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import api from "../../api/client";

export default function VerifyEmail() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const navigate = useNavigate();
  const location = useLocation();

  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const labels = useMemo(
    () => ({
      en: {
        title: "Verifying your email...",
        successTitle: "Email verified",
        successMsg: "Your email has been verified successfully.",
        errorTitle: "Verification failed",
        invalidLink: "Invalid or missing verification link.",
        expired: "This verification link has expired.",
        backToLogin: "Back to login",
        goHome: "Go to home",
        pleaseWait: "Please wait...",
        resend: "Resend verification",
        sending: "Sending...",
        resentSuccess: "A new verification email has been sent.",
        resendFailed: "Failed to resend verification email.",
      },
      ar: {
        title: "جاري تأكيد بريدك الإلكتروني...",
        successTitle: "تم تأكيد البريد الإلكتروني",
        successMsg: "تم تأكيد بريدك الإلكتروني بنجاح.",
        errorTitle: "فشل في تأكيد البريد",
        invalidLink: "رابط التفعيل غير صالح أو مفقود.",
        expired: "انتهت صلاحية رابط التفعيل.",
        backToLogin: "العودة لتسجيل الدخول",
        goHome: "الذهاب للرئيسية",
        pleaseWait: "يرجى الانتظار...",
        resend: "إعادة إرسال التفعيل",
        sending: "جاري الإرسال...",
        resentSuccess: "تم إرسال رابط تفعيل جديد إلى بريدك الإلكتروني.",
        resendFailed: "فشل في إعادة إرسال بريد التفعيل.",
      },
    })[lang] || {
      title: "Verifying your email...",
      successTitle: "Email verified",
      successMsg: "Your email has been verified successfully.",
      errorTitle: "Verification failed",
      invalidLink: "Invalid or missing verification link.",
      expired: "This verification link has expired.",
      backToLogin: "Back to login",
      goHome: "Go to home",
      pleaseWait: "Please wait...",
      resend: "Resend verification",
      sending: "Sending...",
      resentSuccess: "A new verification email has been sent.",
      resendFailed: "Failed to resend verification email.",
    },
    [lang]
  );

  useEffect(() => {
    let cancelled = false;

    const searchParams = new URLSearchParams(location.search || "");
    const hashParams = new URLSearchParams(
      (location.hash || "").replace(/^#/, "")
    );

    const token =
      searchParams.get("token") ||
      searchParams.get("t") ||
      searchParams.get("hash") ||
      hashParams.get("token") ||
      hashParams.get("t") ||
      hashParams.get("hash") ||
      "";

    const savedEmail = localStorage.getItem("verify_email") || "";

    async function resendVerificationEmail() {
      if (!savedEmail) return false;

      try {
        setResending(true);

        await api.apiPost("/resend-verification", {
          email: savedEmail,
        });

        if (cancelled) return false;

        setResent(true);
        return true;
      } catch (e) {
        if (cancelled) return false;
        return false;
      } finally {
        if (!cancelled) {
          setResending(false);
        }
      }
    }

    if (!token) {
      setStatus("error");
      setMessage(labels.invalidLink);
      return;
    }

    async function verify() {
      try {
        setStatus("loading");
        setMessage("");
        setResent(false);

        const data = await api.apiGet(
          `/verify-email?token=${encodeURIComponent(token)}`
        );

        if (cancelled) return;

        if (data?.message) {
          setStatus("success");
          setMessage(data.message || labels.successMsg);

          setTimeout(() => {
            if (!cancelled) {
              navigate("/login", { replace: true });
            }
          }, 1500);

          return;
        }

        throw new Error(labels.errorTitle);
      } catch (err) {
        if (cancelled) return;

        const msg =
          err?.response?.data?.message ||
          err?.message ||
          (typeof err === "string" ? err : "") ||
          labels.errorTitle;

        const lower = String(msg).toLowerCase();

        if (lower.includes("expired")) {
          setStatus("error");
          setMessage(labels.expired);

          const resentOk = await resendVerificationEmail();

          if (!cancelled && resentOk) {
            setMessage(`${labels.expired} ${labels.resentSuccess}`);
          }

          return;
        }

        if (
          lower.includes("invalid") ||
          lower.includes("missing") ||
          lower.includes("token")
        ) {
          setMessage(labels.invalidLink);
        } else {
          setMessage(msg);
        }

        setStatus("error");
      }
    }

    verify();

    return () => {
      cancelled = true;
    };
  }, [location.search, location.hash, labels, navigate]);

  const handleResend = async () => {
    const savedEmail = localStorage.getItem("verify_email") || "";

    if (!savedEmail) {
      setMessage(labels.invalidLink);
      return;
    }

    try {
      setResending(true);
      setResent(false);

      await api.apiPost("/resend-verification", {
        email: savedEmail,
      });

      setResent(true);
      setMessage(labels.resentSuccess);
    } catch (err) {
      setMessage(
        err?.response?.data?.message || labels.resendFailed
      );
    } finally {
      setResending(false);
    }
  };

  const title =
    status === "success"
      ? labels.successTitle
      : status === "error"
      ? labels.errorTitle
      : labels.title;

  return (
    <div className="container py-5" dir={isAr ? "rtl" : "ltr"}>
      <h3>{title}</h3>

      <p>{status === "loading" ? labels.pleaseWait : message}</p>

      {status === "error" && (
        <div className="mb-3">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleResend}
            disabled={resending}
          >
            {resending ? labels.sending : labels.resend}
          </button>

          {resent && (
            <div className="mt-2 text-success">
              {labels.resentSuccess}
            </div>
          )}
        </div>
      )}

      <div className="d-flex gap-3 flex-wrap">
        <Link to="/login">{labels.backToLogin}</Link>
        <Link to="/">{labels.goHome}</Link>
      </div>
    </div>
  );
}